import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactElement,
} from "react";
import { ExternalLink, Info, Pencil } from "lucide-react";
import { useAuth } from "@clerk/react";
import { useQueryClient } from "@tanstack/react-query";
import { toast, Tooltip, TooltipContent, TooltipTrigger } from "@ndd/ui";
import { ApiRequestError, authenticatedApi } from "../../lib/api";
import type { RdHealthieLink } from "../../lib/use-rd-detail";
import { formatTimestamp } from "../../../../shared/local-date";
import { RDAvatar } from "./rd-avatar";

// Under the always theme the RD's name is the page title: 30px display, bold
// (NO-792). Brand utilities, so the classic header is unchanged.
const THEMED_NAME = "brand:font-sans brand:text-[30px] brand:font-bold";

interface RdHeaderStripProps {
  rdId: string;
  /** Whether this viewer may rename the RD; derived by the route from `canRenameRd`. */
  canEdit: boolean;
  initialName: string;
  sessionCount: number;
  createdAt: string;
  supervisorName: string | null;
  /** Absent while the detail read has not answered, so no false gap is shown. */
  healthieLink?: RdHealthieLink;
}

interface RdHeaderStripBodyProps extends RdHeaderStripProps {
  /** Runs after a rename commits, once this RD's detail read is invalidated. */
  onRenamed?: () => void;
}

/**
 * True while a frame above the surface draws the RD header itself (the
 * single-rail frame's RD page, NO-754). Every surface still mounts
 * `RdHeaderStrip`, which then renders nothing, so the header shows once.
 */
export const RdFrameHeaderContext = createContext(false);

export const RD_HEALTHIE_LINK_MISSING_COPY =
  "This RD isn't linked to a Healthie Provider yet. Email justin@nodietdietitian.com to get the link set up.";

/** Opening the field puts the cursor in it with the old name selected. */
function useFocusOnOpen(isOpen: boolean) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isOpen]);

  return inputRef;
}

/** The RD keeps its old name on failure, so the message is all the user gets. */
function reportRenameFailure(error: unknown): void {
  if (error instanceof ApiRequestError) {
    const { error: message } = (error.body ?? {}) as { error?: string };

    toast.error(message ?? "Failed to rename.");

    return;
  }

  toast.error("Something went wrong.");
}

/**
 * Renaming one RD: the draft name, whether the field is open, and the save
 * that commits it through `PATCH /api/rds/:id`. Held apart from the header's
 * layout so each has one job.
 */
function useRdRename(rdId: string, initialName: string, onRenamed?: () => void) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const [name, setName] = useState(initialName);
  const [committedName, setCommittedName] = useState(initialName);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useFocusOnOpen(isEditing);

  function cancel() {
    setName(committedName);
    setIsEditing(false);
  }

  async function save() {
    const trimmed = name.trim();

    if (!trimmed || trimmed === committedName) {
      cancel();

      return;
    }

    setIsSaving(true);

    try {
      await authenticatedApi(getToken, `/api/rds/${rdId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: trimmed }),
      });
      setCommittedName(trimmed);
      setName(trimmed);
      toast.success("Name updated.");
      await queryClient.invalidateQueries({ queryKey: ["rd", rdId] });
      onRenamed?.();
    } catch (error) {
      reportRenameFailure(error);
      setName(committedName);
    } finally {
      setIsSaving(false);
      setIsEditing(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter") {
      e.preventDefault();
      void save();
    } else if (e.key === "Escape") {
      cancel();
    }
  }

  return {
    name,
    setName,
    committedName,
    isEditing,
    startEditing: () => setIsEditing(true),
    isSaving,
    inputRef,
    save,
    handleKeyDown,
  };
}

function headerMeta(
  sessionCount: number,
  createdAt: string,
  supervisorName: string | null
): string {
  return [
    `${sessionCount} session${sessionCount === 1 ? "" : "s"}`,
    `Added ${formatTimestamp(createdAt)}`,
    supervisorName ? `Supervised by ${supervisorName}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

/** A surface's RD header, unless a frame above it already draws one. */
export function RdHeaderStrip(props: RdHeaderStripProps) {
  const frameOwnsHeader = useContext(RdFrameHeaderContext);
  if (frameOwnsHeader) return null;

  return <RdHeaderStripBody {...props} />;
}

export function RdHeaderStripBody({
  rdId,
  canEdit,
  initialName,
  sessionCount,
  createdAt,
  supervisorName,
  healthieLink,
  onRenamed,
}: RdHeaderStripBodyProps) {
  const rename = useRdRename(rdId, initialName, onRenamed);

  return (
    <div className="flex items-center gap-4">
      <RDAvatar name={rename.committedName} size={48} />
      <div className="flex-1 min-w-0">
        {rename.isEditing ? (
          <input
            ref={rename.inputRef}
            value={rename.name}
            onChange={(e) => rename.setName(e.target.value)}
            onBlur={() => void rename.save()}
            onKeyDown={rename.handleKeyDown}
            disabled={rename.isSaving}
            className={`block text-heading font-semibold text-foreground bg-transparent border-b-2 border-primary outline-none w-full disabled:opacity-50 ${THEMED_NAME}`}
          />
        ) : (
          <RdHeaderName
            name={rename.committedName}
            canEdit={canEdit}
            onEdit={rename.startEditing}
          />
        )}
        <RdHeaderMeta
          sessionCount={sessionCount}
          createdAt={createdAt}
          supervisorName={supervisorName}
          healthieLink={healthieLink}
        />
      </div>
    </div>
  );
}

/**
 * The RD name as it reads when nobody is editing it, plus the rename
 * affordance a viewer who may rename gets. Pure and hook-free, so it renders
 * under `tsx --test` without Clerk or TanStack providers.
 *
 * The pencil is present but transparent at rest and revealed on hover of the
 * surrounding group, or on keyboard focus, so it never becomes a target that
 * only a mouse can reach.
 */
export function RdHeaderName({
  name,
  canEdit,
  onEdit,
}: {
  name: string;
  canEdit: boolean;
  onEdit: () => void;
}) {
  if (!canEdit) {
    return (
      <p
        className={`text-heading font-semibold text-foreground leading-tight ${THEMED_NAME}`}
      >
        {name}
      </p>
    );
  }

  return (
    <div className="group flex items-center gap-1.5">
      <button
        type="button"
        onClick={onEdit}
        className={`block text-left text-heading font-semibold text-foreground leading-tight ${THEMED_NAME}`}
      >
        {name}
      </button>
      <button
        type="button"
        onClick={onEdit}
        aria-label={`Rename ${name}`}
        title="Rename"
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
      >
        <Pencil className="h-4 w-4" />
      </button>
    </div>
  );
}

/**
 * The meta line: session count, added date, supervisor, then the Healthie
 * item. The facts truncate while the Healthie item keeps its room, so the link
 * is never clipped away on a narrow header.
 */
export function RdHeaderMeta({
  sessionCount,
  createdAt,
  supervisorName,
  healthieLink,
}: {
  sessionCount: number;
  createdAt: string;
  supervisorName: string | null;
  healthieLink?: RdHealthieLink;
}): ReactElement {
  return (
    <div className="mt-1 flex min-w-0 items-center gap-1 text-sm text-muted-foreground">
      <span className="truncate">
        {headerMeta(sessionCount, createdAt, supervisorName)}
      </span>
      {healthieLink && (
        <span className="flex shrink-0 items-center gap-1">
          <span aria-hidden="true">·</span>
          <RdHealthieLinkItem link={healthieLink} />
        </span>
      )}
    </div>
  );
}

/**
 * The RD's Healthie Provider link, or the gap and who fixes it. Pure and
 * hook-free so it renders under `tsx --test`; the unlinked tooltip needs the
 * app's TooltipProvider.
 */
export function RdHealthieLinkItem({ link }: { link: RdHealthieLink }): ReactElement {
  if (link.status === "linked") {
    return (
      <a
        href={link.url}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 text-primary hover:underline"
      >
        Open in Healthie
        <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
      </a>
    );
  }

  return (
    <span className="inline-flex items-center gap-1">
      No Healthie Provider link
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label="Why this RD has no Healthie Provider link"
            className="inline-flex h-4 w-4 items-center justify-center rounded-full text-subtle hover:text-foreground"
          >
            <Info className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-[260px]">
          {RD_HEALTHIE_LINK_MISSING_COPY}
        </TooltipContent>
      </Tooltip>
    </span>
  );
}
